/**
 * Axios 请求封装
 * - POST 请求自动序列化为 application/x-www-form-urlencoded
 * - 统一错误处理
 * - 响应拦截器统一判断业务 code
 */
import axios from 'axios'
import qs from 'qs'
import { Message } from 'element-ui'
import { getCustomer, clearCustomer, getToken } from './auth'
import router from '@/router'

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL + '/fast/',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
  }
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 携带 token（登录后后端返回的 skey）
    const token = getToken()
    if (token) {
      config.headers.token = token
    }
    // POST 请求自动序列化参数为表单格式（FormData 与 JSON 跳过）
    if (config.method === 'post' && config.data && !(config.data instanceof FormData)) {
      const ct = (config.headers && config.headers['Content-Type']) || ''
      if (!ct.includes('application/json')) {
        config.data = qs.stringify(config.data)
      }
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 网络异常提示节流：多个请求同时失败时只弹一次
let lastNetworkErrorTime = 0

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data
    // 业务状态码判断
    if (res.code && res.code !== '00000') {
      // 登录过期 / 未认证
      if (res.code === '10001' || res.code === '10002' || res.code === '40100') {
        // 登录、注册等公共页面：未登录访问需授权接口时，不弹“登录过期”、不强制跳转
        const curPath = router.currentRoute && router.currentRoute.path
        if (curPath === '/login' || curPath === '/register') {
          return Promise.reject(markHandled(new Error(res.msg)))
        }
        Message.error('登录已过期，请重新登录')
        clearCustomer()
        router.push('/login')
        return Promise.reject(markHandled(new Error(res.msg)))
      }
      return res
    }
    return res
  },
  error => {
    // 网络层失败（后端未启动/地址错误/跨域/超时）：3 秒内只提示一次
    const now = Date.now()
    if (now - lastNetworkErrorTime > 3000) {
      lastNetworkErrorTime = now
      Message.error('网络请求失败，请稍后重试')
    }
    return Promise.reject(markHandled(error))
  }
)

/** 标记错误已由拦截器统一提示，全局 unhandledrejection 据此静默 */
function markHandled (error) {
  error.__handled = true
  return error
}

/**
 * GET 请求
 * @param {string} url
 * @param {Object} params
 * @returns {Promise}
 */
export function get (url, params = {}) {
  return service.get(url, { params })
}

/**
 * POST 请求（表单格式）
 * @param {string} url
 * @param {Object} data
 * @returns {Promise}
 */
export function post (url, data = {}) {
  return service.post(url, data)
}

/**
 * POST 请求（JSON 格式）
 * @param {string} url
 * @param {Object} data
 * @returns {Promise}
 */
export function postJson (url, data = {}) {
  return service.post(url, data, {
    headers: { 'Content-Type': 'application/json;charset=UTF-8' }
  })
}

/**
 * 上传文件（FormData）
 * @param {string} url
 * @param {FormData} formData
 * @returns {Promise}
 */
export function upload (url, formData) {
  return service.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export default service
